import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

// Shared parts for the investment dashboard.
//
// MOBILE IS THE PRIMARY TARGET, not a fallback. Every figure block below is built to work at
// 375px first and widen from there; nothing relies on a wide table being scrolled sideways,
// because a number you have to scroll to find cannot be compared with the one it sits beside.
//
// THE READER IS A SHAREHOLDER, NOT AN ANALYST. On a phone the questions are, in order: what
// am I worth, what changed, how much can I get to in cash, what is at risk. Everything else
// is detail and lives behind a tap. That is why the primitives here come in two registers —
// a few large, plain figures (Headline, Figure, PerfTriple) for the questions above, and the
// denser blocks (PairedBars, tables) for the reader who goes looking.

export const TONE = {
  navy: { bar: '#1e3a5f', text: 'text-[#1e3a5f] dark:text-[#7da2cc]', edge: 'border-l-[#1e3a5f]' },
  teal: { bar: '#0f766e', text: 'text-teal-700 dark:text-teal-400', edge: 'border-l-teal-700' },
  gold: { bar: '#b8860b', text: 'text-amber-700 dark:text-amber-500', edge: 'border-l-amber-600' },
};

export const fmtM = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString()}`);
export const fmtPct = (v, dp = 1) => (v == null ? '—' : `${v.toFixed(dp)}%`);
export const fmtSigned = (v, dp = 1) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(dp)}`);
// Sign is always explicit on money that can go either way — "+150" and "-35" rather than a
// bare number whose direction you infer from the colour. Colour is the fast read; the glyph
// is the one that survives a monochrome print and a colour-blind reader.
export const fmtSignedM = (v) => (v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '-' : ''}${fmtM(Math.abs(v))}`);
export const fmtSignedPct = (v, dp = 1) => (v == null ? '—' : `${fmtSigned(v, dp)}%`);

/** Green up, red down — the house rule for anything that can go either way. */
export const signClass = (v) => (v == null ? 'text-gray-400'
  : v > 0 ? 'text-green-700 dark:text-green-400'
    : v < 0 ? 'text-red-700 dark:text-red-400'
      : 'text-gray-600 dark:text-gray-300');

/** THE ONE THING ON THIS SCREEN SAYING THE FIGURES ARE INVENTED.
 *
 * Solid, high-contrast, and STICKY, so it does not scroll away and leave a page of
 * authoritative-looking numbers with nothing qualifying them. A muted tint reads as a note;
 * this has to read as a stop. It renders above the tab strip on every tab, because a reader
 * can land on any of them.
 *
 * A tiled diagonal watermark used to sit behind the figures as well, to travel with a
 * cropped screenshot. It was removed at the owner's instruction — it made the numbers harder
 * to read and the screen harder to review, which was costing more than the residual
 * screenshot risk was worth. That leaves this banner carrying the warning alone, so it
 * matters MORE than it did: do not shrink it, soften it, make it dismissible, or move it
 * below the figures. Remove it only when every panel is fed by a real source.
 */
export const Illustrative = () => (
  <div className="sticky top-0 z-30 mb-4 rounded-xl border-2 border-amber-500 bg-amber-100 px-4 py-3 shadow-sm dark:border-amber-500/70 dark:bg-amber-950/80 dark:backdrop-blur">
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
      <span className="rounded bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
        Illustrative
      </span>
      <span className="text-[14px] font-bold text-amber-950 dark:text-amber-100">
        These are not real figures.
      </span>
    </div>
    <p className="mt-1.5 text-[12px] leading-relaxed text-amber-900 dark:text-amber-200/90">
      Nothing on this dashboard is a real holding, valuation, return or limit. The layout is
      being specified; no live data is connected. Do not use, quote or circulate any number
      from this screen.
    </p>
  </div>
);

export const Panel = ({ title, right, children, className = '' }) => (
  <section className={`rounded-xl border border-gray-200 dark:border-gray-800 ${className}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-gray-100 px-4 py-3 dark:border-gray-800/60">
      <h2 className="text-[10px] font-medium uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400">
        {title}
      </h2>
      {right && <span className="text-[11px] text-gray-400">{right}</span>}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

/** Two up on a phone, four across from `sm`. A single column would push the fourth tile
 *  below the fold on every handset; four would make each one 90px wide. */
export const KpiGrid = ({ children }) => (
  <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4">{children}</div>
);

export const Kpi = ({ label, value, sub, tone = 'navy', sign }) => (
  <div className={`rounded-xl border border-l-[3px] border-gray-200 bg-white px-3.5 py-3 dark:border-gray-800 dark:bg-gray-900/40 ${TONE[tone].edge}`}>
    <div className="text-[9.5px] font-medium uppercase leading-tight tracking-[0.15em] text-gray-400">
      {label}
    </div>
    <div className={`mt-1.5 font-serif text-[19px] leading-none tabular-nums sm:text-2xl ${
      sign != null ? signClass(sign) : TONE[tone].text}`}>
      {value}
    </div>
    {sub && <div className="mt-1.5 text-[10.5px] leading-tight text-gray-400">{sub}</div>}
  </div>
);

/** Return, benchmark and alpha, as three cells.
 *
 * THE SAME COMPONENT ON THE OVERVIEW AND IN THE BOOKS TAB, so a book's performance and its
 * sub-portfolios' performance cannot come to read differently — the Overview shows this per
 * book and the Books tab shows it per line, and they are the same figures at two depths.
 *
 * ALPHA IS THE PAYOFF AND READS FIRST: it carries the brand accent, a slightly larger figure
 * and the green/red sign, because "did we beat it" is the question the other two cells exist
 * to answer. The accent is dropped when there is no alpha — a gold-ringed cell containing an
 * em-dash would draw the eye hardest to the one thing that is not there.
 *
 * A MISSING FIGURE IS AN EM-DASH, NEVER A ZERO. `fmtPct(null)` already gives "—"; the caller
 * must pass null rather than a fallback. Two of the three books are not benchmarked and the
 * dashes are how a reader sees that at a glance.
 *
 * Spans throughout, with `grid` on a span: this renders inside TapRow, which is a <button>,
 * and a <div> is not phrasing content.
 */
export const PerfTriple = ({ ret, bmk, alpha, className = '' }) => (
  <span className={`grid grid-cols-3 gap-1.5 ${className}`}>
    <PerfCell label="Return" value={fmtPct(ret)} />
    <PerfCell label="Benchmark" value={fmtPct(bmk)} />
    <PerfCell label="Alpha" value={alpha == null ? '—' : `${fmtSigned(alpha)} pp`}
              sign={alpha} accent={alpha != null} />
  </span>
);

const PerfCell = ({ label, value, sign, accent }) => (
  <span className={`block min-w-0 rounded-lg px-2 py-1.5 ${accent
    ? 'bg-brand-gold/10 ring-1 ring-inset ring-brand-gold/25'
    : 'bg-gray-50 dark:bg-gray-800/50'}`}>
    <span className={`block truncate text-[9px] font-medium uppercase tracking-[0.1em] ${
      accent ? 'text-brand-gold' : 'text-gray-400'}`}>
      {label}
    </span>
    <span className={`mt-0.5 block tabular-nums ${accent ? 'text-[13.5px] font-semibold' : 'text-[12.5px]'} ${
      sign != null ? signClass(sign)
        : value === '—' ? 'text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>
      {value}
    </span>
  </span>
);

/** Portfolio against its benchmark, one pair per line.
 *
 * The mockup draws this as a grouped column chart. Columns are the wrong form on a phone:
 * five pairs across 375px gives each bar about 28px and turns the category labels vertical.
 * Horizontal paired bars carry the same comparison, read at any width, and put the label in
 * the reading direction — the gap between the two bars is still the whole point.
 */
export const PairedBars = ({ rows, max }) => (
  <div className="flex flex-col gap-3">
    {rows.map((r) => (
      <div key={r.name}>
        <div className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 truncate text-[12.5px] text-gray-700 dark:text-gray-300">{r.name}</span>
          <span className="shrink-0 text-[11px] tabular-nums text-gray-400">
            {fmtPct(r.a)} <span className="text-gray-300 dark:text-gray-600">vs</span> {fmtPct(r.b)}
            <span className={`ml-1.5 font-semibold ${signClass(r.a - r.b)}`}>
              {fmtSigned(r.a - r.b)}
            </span>
          </span>
        </div>
        <div className="mt-1 flex flex-col gap-1">
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
            <div className="h-full rounded-full" style={{ width: `${(r.a / max) * 100}%`, background: TONE.navy.bar }} />
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
            <div className="h-full rounded-full bg-gray-300 dark:bg-gray-600"
                 style={{ width: `${(r.b / max) * 100}%` }} />
          </div>
        </div>
      </div>
    ))}
    <div className="flex items-center gap-4 pt-1 text-[10px] text-gray-400">
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-4 rounded-full" style={{ background: TONE.navy.bar }} />Portfolio
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-4 rounded-full bg-gray-300 dark:bg-gray-600" />Benchmark
      </span>
    </div>
  </div>
);

// --- the shareholder register: few numbers, large, with the change beside them -----------

/** The single dominant figure on a screen. There is only ever one per tab.
 *
 * At 375px this is the whole first impression, so it is sized to be read at arm's length
 * (36px serif, roughly three times the body figures) and the unit is demoted to a suffix —
 * "AED" and "m" carry no information a reader of this page does not already have, and giving
 * them the same weight as the digits is what made the old KPI grid read as four identical
 * grey cards.
 */
export const Headline = ({ label, value, unit, children }) => (
  <div>
    <div className="text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">{label}</div>
    <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
      <span className="font-serif text-[36px] leading-none tabular-nums text-gray-900 sm:text-5xl dark:text-gray-100">
        {value}
      </span>
      {unit && <span className="text-[12px] text-gray-400">{unit}</span>}
    </div>
    {children}
  </div>
);

/** A supporting figure: a number, what it is, and nothing else.
 *
 * Deliberately NOT a bordered card. Four bordered tiles in a grid all read as equally
 * important, which is what buried the total wealth figure on the old Overview; these sit in
 * the same container as the headline they qualify, separated by a rule rather than a box.
 */
export const Figure = ({ label, value, sign, sub, className = '' }) => (
  <div className={`min-w-0 ${className}`}>
    <div className="text-[9.5px] font-medium uppercase leading-tight tracking-[0.14em] text-gray-400">
      {label}
    </div>
    <div className={`mt-1 font-serif text-[19px] leading-none tabular-nums sm:text-[22px] ${
      sign != null ? signClass(sign) : 'text-gray-900 dark:text-gray-100'}`}>
      {value}
    </div>
    {sub && <div className="mt-1 text-[10.5px] leading-tight text-gray-400">{sub}</div>}
  </div>
);

/** The value trail as one stroke.
 *
 * A shareholder asking "what changed" is helped more by the SHAPE of the year than by a
 * second percentage — it says whether the gain was a steady climb or one good month, which
 * no single figure can. Kept axis-free and label-free on purpose: at 375px an axis costs
 * more width than it returns, and the two endpoints are already stated in words beside it.
 *
 * `preserveAspectRatio="none"` lets the path fill any width; `vector-effect` keeps the
 * stroke from being stretched with it, which is what makes a non-uniform scale look wrong.
 */
export const Sparkline = ({ points, height = 44 }) => {
  const vals = points.map((p) => p.aum);
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const span = hi - lo || 1;
  const x = (i) => (i / (points.length - 1 || 1)) * 100;
  const y = (v) => 30 - ((v - lo) / span) * 26 - 2;
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(2)},${y(p.aum).toFixed(2)}`).join(' ');
  const rising = vals[vals.length - 1] >= vals[0];
  const stroke = rising ? '#15803d' : '#b91c1c';
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true"
         className="w-full" style={{ height }}>
      <path d={`${d} L100,30 L0,30 Z`} fill={stroke} fillOpacity="0.08" stroke="none" />
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round"
            strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

/** Detail that is present but not presented.
 *
 * Collapsed on a phone and open from `sm` up, and the toggle itself only exists on a phone.
 * Done with visibility classes rather than a media-query hook so there is no breakpoint
 * state in JS to disagree with the CSS: at ≥640px the content is `sm:block` regardless of
 * what the button last set, which is the behaviour wanted — a desk reader should never have
 * to expand anything.
 */
export const Disclosure = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3 text-left sm:hidden dark:border-gray-800"
      >
        <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400">
          {label}
        </span>
        <span className="shrink-0 text-[11px] text-gray-400">{open ? 'Hide ▴' : 'Show ▾'}</span>
      </button>
      <div className={open ? 'mt-3' : 'hidden sm:block'}>{children}</div>
    </div>
  );
};

/** Detail that is closed on load at EVERY width, unlike Disclosure which opens from `sm`.
 *
 * For the case where the detail is genuinely secondary rather than merely space-constrained:
 * a desk reader should not meet it either, and no breakpoint changes that. Kept deliberately
 * plain — a label and a caret, no border or panel chrome, so a closed one is a line of text
 * rather than another card competing with the figures above it.
 */
export const Collapse = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
              className="flex w-full items-center justify-between gap-3 py-1 text-left">
        <span className="text-[9.5px] font-medium uppercase tracking-[0.14em] text-gray-400">
          {label}
        </span>
        <span aria-hidden="true" className="shrink-0 text-[11px] text-gray-400">
          {open ? '▴' : '▾'}
        </span>
      </button>
      {open && <div className="mt-1">{children}</div>}
    </div>
  );
};

/** A row that opens something.
 *
 * The chevron is always visible rather than appearing on hover, because hover does not exist
 * on a phone and it is the only thing telling a reader the row is a door. `label` is the
 * accessible name, spelled out by the caller — the row's visible content is a pile of
 * numbers, which reads as gibberish to a screen reader announcing a button.
 *
 * Children must be phrasing content (spans, not divs): this is a <button>.
 */
export const TapRow = ({ onClick, label, children, className = '' }) => (
  <button
    type="button" onClick={onClick} aria-label={label}
    className={`flex w-full items-center gap-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40 ${className}`}
  >
    <span className="min-w-0 flex-1">{children}</span>
    <span aria-hidden="true" className="shrink-0 text-[13px] text-gray-300 dark:text-gray-600">›</span>
  </button>
);

/** The drill-down container, lifted from the research portal's scorecard dialog so the two
 *  screens open detail the same way.
 *
 * Bottom sheet on a phone, centred card from `sm`. Through a PORTAL onto <body>: a fixed
 * overlay is positioned against the nearest ancestor carrying a transform, not against the
 * viewport, and the dashboard sits under a page shell that may well gain one — a portal has
 * no ancestors to be trapped by, so this cannot regress silently.
 *
 * The header is outside the scrolling region, so the title and the close control stay put
 * while a long holdings list runs under them. Escape closes the whole sheet even when a
 * reader has drilled two levels in — one key, one meaning; `onBack` is the deliberate step.
 */
export const Sheet = ({ open, title, meta, eyebrow, onBack, onClose, children }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    // Without the lock the page behind scrolls under the sheet on a trackpad and on a
    // rubber-banding phone, which makes the whole thing feel unmoored.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal((
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4"
         onClick={onClose} role="presentation">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-[3px]" />
      <div className="relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl ring-1 ring-black/5 sm:max-h-[86vh] sm:max-w-2xl sm:rounded-2xl dark:bg-gray-900 dark:ring-white/10"
           onClick={(e) => e.stopPropagation()} role="presentation">
        {/* Grab handle, phone only — it says "this pulls up from the bottom". */}
        <div className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-gray-300 sm:hidden dark:bg-gray-700" />
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-4 pb-3 pt-3 sm:px-5 sm:pb-4 sm:pt-4 dark:border-gray-800/60">
          <div className="min-w-0">
            {(onBack || eyebrow) && (
              <div className="mb-1 flex items-center gap-2">
                {onBack && (
                  <button type="button" onClick={onBack}
                          className="-ml-1 rounded px-1 text-[11px] text-gray-400 hover:text-brand-gold">
                    ‹ Back
                  </button>
                )}
                {eyebrow && (
                  <span className="min-w-0 truncate text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">
                    {eyebrow}
                  </span>
                )}
              </div>
            )}
            <h2 className="font-serif text-[19px] leading-tight text-gray-900 sm:text-2xl dark:text-gray-100">
              {title}
            </h2>
            {meta && <div className="mt-1 text-[11px] text-gray-400">{meta}</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="shrink-0 rounded-lg px-2 py-1 text-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5">{children}</div>
      </div>
    </div>
  ), document.body);
};

export const Tabs = ({ tabs, active, onChange }) => (
  <nav className="scrollbar-thin -mx-1 mb-4 flex gap-1 overflow-x-auto border-b border-gray-200 px-1 dark:border-gray-800">
    {tabs.map((t) => (
      <button
        key={t.key} type="button" onClick={() => onChange(t.key)}
        className="-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors"
        style={active === t.key
          ? { borderColor: 'rgb(var(--brand-gold-rgb))', color: 'rgb(var(--brand-gold-rgb))' }
          : { borderColor: 'transparent', color: '#6b7280' }}
      >
        {t.label}
      </button>
    ))}
  </nav>
);
